import React, { useEffect, useState } from 'react';
import { ADVISORY_FOOTER, BAND_LABELS, IntegrityConfidence, RecordingPlayer, ReportView, TranscriptViewer, verdictLabel } from '../Phase3InterviewDetail';

// The Alex report inside the HM candidate page (bundle 5b): Phase 3's own views, read-only — Overview (score, band,
// verdict), HM review (the video + the hiring manager report) and Transcript. The decision is the page's own box.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const P3 = `${API_BASE}/api/phase3`;
const SUB = [['overview', 'Overview'], ['hm', 'HM review'], ['transcript', 'Transcript']];

const get = async (path) => {
  const res = await fetch(`${P3}${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  return res.ok ? res.json() : Promise.reject(Object.assign(new Error(String(res.status)), { status: res.status }));
};

export default function AlexReport({ interviewId }) {
  const [sub, setSub] = useState('overview');
  const [iv, setIv] = useState(null);
  const [reports, setReports] = useState([]);
  const [transcript, setTranscript] = useState(null);
  const [rec, setRec] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    get(`/interview/${interviewId}`).then((r) => setIv(r.data || null)).catch(() => setError('The AI screen report isn’t available.'));
    get(`/interview/${interviewId}/reports`).then((r) => setReports((r.data && r.data.reports) || [])).catch(() => {});
  }, [interviewId]);
  const loadRec = () => {
    setRec({ loading: true });
    Promise.allSettled(['video', 'audio', 'merged'].map((k) => get(`/interview/${interviewId}/recording/${k}`))).then(([v, a, m]) => setRec({
      videoUrl: v.status === 'fulfilled' ? (v.value.data || {}).url : null, videoOffsetMs: v.status === 'fulfilled' ? Number((v.value.data || {}).video_offset_ms) || 0 : 0,
      audioUrl: a.status === 'fulfilled' ? (a.value.data || {}).url : null, mergedUrl: m.status === 'fulfilled' ? (m.value.data || {}).url : null,
      error: v.status !== 'fulfilled' && a.status !== 'fulfilled' ? 'No recordings available for this interview.' : '' }));
  };
  useEffect(() => {
    if (sub === 'transcript' && !transcript) get(`/interview/${interviewId}/transcript`).then((r) => setTranscript(r.data || { has_transcript: false, turns: [] })).catch(() => setTranscript({ has_transcript: false, turns: [] }));
    if (sub === 'hm' && !rec) loadRec();
  }, [sub, interviewId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <p className="text-sm text-[#64748B]">{error}</p>;
  if (!iv) return <p className="text-sm text-[#64748B]" aria-busy="true">Loading the AI screen…</p>;
  const hmReport = reports.find((r) => r.report_type === 'HIRING_MANAGER');
  const a = iv.assessment || {};
  return (
    <div className="space-y-4" data-testid="alex-report">
      <div className="flex gap-1 p-1 bg-[#EEF1F6] rounded-xl w-max max-w-full overflow-x-auto" role="tablist" aria-label="AI screen">
        {SUB.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={sub === k} onClick={() => setSub(k)} data-testid={`alex-sub-${k}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap ${sub === k ? 'bg-white text-[#0F172A] shadow' : 'text-[#475569]'}`}>{l}</button>))}
      </div>
      {sub === 'overview' && (
        <section className="rounded-xl border border-[#E6E9F0] bg-white p-4" data-testid="alex-overview">
          {iv.overall_score != null ? <>
            <p className="text-sm"><span className="text-3xl font-black">{iv.overall_score}</span> <span className="text-[#64748B]">/ 400</span>
              {a.skill_band && <span className="ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#EEF2FF] text-[#3730A3]">{BAND_LABELS[a.skill_band] || a.skill_band}</span>}</p>
            {iv.recommendation && <p className="text-sm mt-1"><b>{verdictLabel(iv.recommendation, a.verdict_reason)}</b></p>}
            {a.verdict_reason && a.verdict_reason.review && <p className="text-xs text-[#B45309] mt-1">{a.verdict_reason.review}</p>}
            <div className="flex flex-wrap gap-4 text-sm text-[#475569] mt-2"><IntegrityConfidence value={iv.final_confidence} />
              <span>Duration: {iv.actual_duration_minutes ? `${iv.actual_duration_minutes} min` : '—'}</span></div>
          </> : <p className="text-sm text-[#64748B]">Not scored yet ({String(iv.status || '').toLowerCase()}).</p>}
          <p className="text-xs text-[#64748B] mt-3">{ADVISORY_FOOTER}</p>
        </section>)}
      {sub === 'hm' && <>
        {rec && <RecordingPlayer videoUrl={rec.videoUrl} audioUrl={rec.audioUrl} mergedUrl={rec.mergedUrl} mergePending={false} videoOffsetMs={rec.videoOffsetMs || 0}
          isLoading={!!rec.loading} error={rec.error} onRetry={loadRec} deleteAfter={iv.video_delete_after} videoMissingReason="" />}
        {hmReport ? <ReportView report={hmReport} type="Hiring Manager" interviewId={iv.id} apiBase={API_BASE} questionSources={iv.question_sources} assessment={iv.assessment} />
          : <p className="text-sm text-[#64748B]">The hiring manager report isn’t ready yet.</p>}
      </>}
      {sub === 'transcript' && (transcript ? <TranscriptViewer transcript={transcript} candidateName={iv.candidate_name} /> : <p className="text-sm text-[#64748B]">Loading…</p>)}
    </div>
  );
}
